/* eslint-disable max-lines -- picker list and panel placement variants share one module */

import { Button } from '@heidi/design-system/button';
import { Routines } from '@heidi/design-system/icons';
import { typographyVariant } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ArrowRight, SlashSquare, Video } from 'lucide-react';
import { useEffect, useRef, useState, type RefObject, type WheelEvent } from 'react';
import { createPortal } from 'react-dom';
import { defineMessages, useIntl } from 'react-intl';
import { useSpotlightSlashPanelLayout } from '../../../data/projects/components/useSpotlightSlashPanelLayout';
import type {
  SpotlightSlashItem,
  SpotlightSlashSection,
} from '../../../data/projects/lib/spotlight-slash-items';
import {
  isSpotlightActionItem,
  isSpotlightWorkflowItem,
} from '../../../data/projects/lib/spotlight-slash-items';
import { ComposerMenuRow } from './LinkContextButton.parts';

const spotlightSlashSectionMessages = defineMessages({
  workflows: {
    defaultMessage: 'Routines',
    id: 'KW9AGXX793',
    description: 'Section heading for routines in the composer slash command picker',
  },
  shortcuts: {
    defaultMessage: 'Shortcuts',
    id: '1Ifuih0rOI',
    description: 'Section heading for shortcuts in the composer slash command picker',
  },
  browseShortcuts: {
    defaultMessage: 'Browse',
    id: 'gYFLJXDgVc',
    description:
      'Link on the Shortcuts header in the slash menu that opens the shortcut marketplace',
  },
});

const WHEEL_DELTA_LINE = 1;
const WHEEL_DELTA_PAGE = 2;

/** Shared by every labelled heading so Routines and Shortcuts stay aligned. */
const SPOTLIGHT_SLASH_SECTION_HEADER_CLASS = cn(
  typographyVariant({ variant: 'caption' }),
  'flex items-center justify-between gap-2 px-3 pb-1 pt-2 font-normal text-text-tertiary'
);

export interface SpotlightSlashPickerListProps {
  readonly sections: readonly SpotlightSlashSection[];
  readonly flatItems: readonly SpotlightSlashItem[];
  readonly onSelect: (item: SpotlightSlashItem) => void;
  readonly activeIndex?: number;
  readonly onActiveIndexChange?: (index: number) => void;
  readonly listboxId?: string;
  readonly optionId?: (index: number) => string;
  readonly ariaLabel?: string;
  /** Viewport-aware cap for the panel's scroll box (px). */
  readonly listMaxHeight: number;
  readonly onBrowseShortcuts?: () => void;
}

function itemKey(item: SpotlightSlashItem, index: number): string {
  switch (item.kind) {
    case 'action':
      return `action:${item.actionId}`;
    case 'workflow':
      return `workflow:${item.skill.skillId}`;
    case 'evidence_shortcut':
      return `shortcut:${item.id}`;
    default:
      return `row:${index}`;
  }
}

function ItemIcon({ item }: { item: SpotlightSlashItem }): JSX.Element {
  if (isSpotlightActionItem(item)) {
    // Create routine gets the branded routines mark; Record keeps the video glyph.
    return item.actionId === 'record' ? (
      <Video className="size-4 shrink-0 text-text-tertiary" aria-hidden />
    ) : (
      <Routines className="size-4 shrink-0 text-text-tertiary" aria-hidden />
    );
  }
  if (isSpotlightWorkflowItem(item)) {
    return <Routines className="size-4 shrink-0 text-text-tertiary" aria-hidden />;
  }
  return (
    <SlashSquare
      className="size-4 shrink-0 text-text-tertiary"
      aria-hidden
      data-testid="spotlight-slash-shortcut-icon"
    />
  );
}

function SectionHeader({
  sectionId,
  onBrowseShortcuts,
  onMouseEnter,
}: {
  sectionId: Exclude<SpotlightSlashSection['id'], 'actions'>;
  onBrowseShortcuts?: () => void;
  onMouseEnter: () => void;
}): JSX.Element {
  const intl = useIntl();
  const showBrowse = sectionId === 'shortcuts' && onBrowseShortcuts;

  return (
    <div
      className={SPOTLIGHT_SLASH_SECTION_HEADER_CLASS}
      data-testid={`spotlight-slash-section-header-${sectionId}`}
      onMouseEnter={onMouseEnter}
    >
      <span>{intl.formatMessage(spotlightSlashSectionMessages[sectionId])}</span>
      {showBrowse ? (
        <Button
          type="button"
          variant="link"
          size="xs"
          disableAutomaticTypographyClasses
          className={cn(
            typographyVariant({ variant: 'caption' }),
            'h-auto min-h-0 min-w-0 gap-0.5 p-0 font-normal text-text-secondary hover:text-text-primary hover:no-underline'
          )}
          onMouseDown={(event) => {
            if (event.button !== 0) return;
            event.preventDefault();
          }}
          onClick={onBrowseShortcuts}
        >
          {intl.formatMessage(spotlightSlashSectionMessages.browseShortcuts)}
          <ArrowRight className="size-3 rtl:-scale-x-100" aria-hidden />
        </Button>
      ) : null}
    </div>
  );
}

export const SpotlightSlashPickerList = ({
  sections,
  flatItems,
  onSelect,
  activeIndex,
  onActiveIndexChange,
  listboxId,
  optionId,
  ariaLabel,
  listMaxHeight,
  onBrowseShortcuts,
}: SpotlightSlashPickerListProps): JSX.Element => {
  const intl = useIntl();
  const activeOptionRef = useRef<HTMLButtonElement | null>(null);
  // The header (including Browse) is not a row. While the pointer is there,
  // hide the row highlight; arrow keys change `activeIndex` and show it again.
  const [headerHovered, setHeaderHovered] = useState(false);
  let flatIndex = 0;

  useEffect(() => {
    setHeaderHovered(false);
  }, [activeIndex]);

  useEffect(() => {
    activeOptionRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  const empty = flatItems.length === 0;

  return (
    <div>
      <ul
        id={listboxId}
        data-spotlight-slash-picker-scroll=""
        className="touch-pan-y overflow-y-auto overscroll-contain scrollbar-none"
        style={{ maxHeight: listMaxHeight }}
        data-testid="spotlight-slash-picker-list"
        aria-label={ariaLabel}
      >
        {empty ? (
          <li
            className={cn(
              typographyVariant({ variant: 'p2' }),
              'px-3 py-4 font-normal text-text-tertiary'
            )}
          >
            {intl.formatMessage({
              defaultMessage: 'No matches',
              id: '0g7LEUVO9J',
              description: 'Empty state when slash spotlight search returns nothing',
            })}
          </li>
        ) : (
          sections.map((section) => (
            <li key={section.id}>
              {/* Actions (Create routine / Record) sit at the top with no heading. */}
              {section.id !== 'actions' ? (
                <SectionHeader
                  sectionId={section.id}
                  onBrowseShortcuts={onBrowseShortcuts}
                  onMouseEnter={() => setHeaderHovered(true)}
                />
              ) : null}
              <ul>
                {section.items.map((item) => {
                  const index = flatIndex++;
                  const isActive = !headerHovered && activeIndex === index;
                  // Shortcuts show their description under the title; routines and
                  // actions show their `/slug` instead (never both).
                  const sub =
                    item.kind === 'evidence_shortcut' && item.sub ? item.sub : undefined;
                  const secondary =
                    isSpotlightActionItem(item) || isSpotlightWorkflowItem(item)
                      ? `/${item.slug}`
                      : sub;
                  return (
                    <li key={itemKey(item, index)}>
                      <ComposerMenuRow
                        ref={isActive ? activeOptionRef : undefined}
                        id={optionId?.(index)}
                        selected={isActive}
                        icon={<ItemIcon item={item} />}
                        title={item.label}
                        secondary={secondary}
                        // Mouse-down (pre-blur) so the editor keeps focus through select.
                        onMouseDown={(event) => {
                          if (event.button !== 0) return;
                          event.preventDefault();
                          onSelect(item);
                        }}
                        onMouseEnter={() => {
                          setHeaderHovered(false);
                          onActiveIndexChange?.(index);
                        }}
                        data-testid={`spotlight-slash-picker-item-${index}`}
                      />
                    </li>
                  );
                })}
              </ul>
            </li>
          ))
        )}
      </ul>
    </div>
  );
};

export type SpotlightSlashPickerPanelProps = Omit<
  SpotlightSlashPickerListProps,
  'ariaLabel' | 'listMaxHeight'
> & {
  /** Where the floating panel anchors relative to the composer input. */
  readonly placement?: 'above' | 'below';
  /** Composer element used to position the portaled panel. */
  readonly anchorRef: RefObject<HTMLElement | null>;
};

const spotlightPanelShellClass =
  'pointer-events-auto overflow-hidden rounded-2xl border border-border bg-surface p-2 shadow-lg';

function SpotlightSlashPickerPanelShell({
  anchorRef,
  placement,
  followMotion = false,
  ariaLabel,
  ...props
}: SpotlightSlashPickerPanelProps & {
  ariaLabel: string;
  followMotion?: boolean;
}): JSX.Element | null {
  const layout = useSpotlightSlashPanelLayout(
    anchorRef,
    placement ?? 'above',
    true,
    followMotion
  );

  if (!layout || typeof document === 'undefined') return null;

  return createPortal(
    <div
      data-testid="spotlight-slash-picker-panel"
      onWheelCapture={keepSpotlightPanelWheelInside}
      className={spotlightPanelShellClass}
      style={layout.panelStyle}
    >
      <SpotlightSlashPickerList
        {...props}
        ariaLabel={ariaLabel}
        listMaxHeight={layout.listMaxHeight}
      />
    </div>,
    document.body
  );
}

export const SpotlightSlashPickerPanel = ({
  placement = 'above',
  ...props
}: SpotlightSlashPickerPanelProps): JSX.Element | null => {
  const intl = useIntl();
  const ariaLabel = intl.formatMessage({
    defaultMessage: 'Search routines and shortcuts',
    id: 'aAFh+7splB',
    description: 'Accessible label for the composer slash spotlight picker',
  });

  return (
    <SpotlightSlashPickerPanelShell
      {...props}
      placement={placement}
      ariaLabel={ariaLabel}
    />
  );
};

/** Full-width panel anchored to a composer card (Work home — opens downward). */
export const SpotlightSlashAnchoredBelowPanel = ({
  placement = 'below',
  ...props
}: SpotlightSlashPickerPanelProps): JSX.Element | null => {
  const intl = useIntl();
  const ariaLabel = intl.formatMessage({
    defaultMessage: 'Search routines and shortcuts',
    id: 'aAFh+7splB',
    description: 'Accessible label for the composer slash spotlight picker',
  });

  return (
    <SpotlightSlashPickerPanelShell
      {...props}
      placement={placement}
      followMotion
      ariaLabel={ariaLabel}
    />
  );
};

function keepSpotlightPanelWheelInside(event: WheelEvent<HTMLElement>): void {
  event.stopPropagation();

  const list = event.currentTarget.querySelector<HTMLElement>(
    '[data-spotlight-slash-picker-scroll]'
  );
  if (!list) return;

  const maxScrollTop = list.scrollHeight - list.clientHeight;
  if (maxScrollTop <= 0) return;

  event.preventDefault();
  const deltaY = normalizeWheelDeltaY(event, list.clientHeight);
  list.scrollTop = Math.max(0, Math.min(maxScrollTop, list.scrollTop + deltaY));
}

function normalizeWheelDeltaY(
  event: WheelEvent<HTMLElement>,
  pageHeight: number
): number {
  if (event.deltaMode === WHEEL_DELTA_LINE) return event.deltaY * 16;
  if (event.deltaMode === WHEEL_DELTA_PAGE) return event.deltaY * pageHeight;
  return event.deltaY;
}
